import { useCallback, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '@terra/ui-design-system';
import { useProjectStore, useSelectedTarget } from '../store/projectStore';
import {
  useManualTrackCredentialsStore,
  type ManualTrackExecutor,
  type ManualTrackOptions,
} from '../store/manualTrackCredentialsStore';
import { useTracking } from './useTracking';
import type { ConfigureCredentialsResult } from '../components/dialogs/ConfigureCredentialsDialog';
import { isApplicationActivated } from '../utils/isApplicationActivated';

export function useManualTrackWithCredentials() {
  const t = useTranslations('proxy.tracking.configureCredentialsDialog');
  const tTracking = useTranslations('proxy.tracking');
  const {
    isConfigureCredentialsOpen,
    pendingExecute,
    setIsConfigureCredentialsOpen,
    setPendingExecute,
    clearPendingExecute,
  } = useManualTrackCredentialsStore();

  const isAwaitingManualTrackCredentials =
    isConfigureCredentialsOpen && pendingExecute !== undefined;

  const trackingCredentialId = useProjectStore(
    (state) => state.trackingCredentialId,
  );
  const selectedTarget = useSelectedTarget();
  const { startTracking, isTrackingEnabled } = useTracking();
  const isRunningPendingTrackRef = useRef(false);

  const defaultCredentialId = trackingCredentialId ?? '';

  const executeManualTrack = useCallback(
    async ({
      execute,
      credentialId,
    }: {
      execute: ManualTrackExecutor;
      credentialId?: string;
    }): Promise<boolean> => {
      try {
        return await execute({ credentialId });
      } catch (error: unknown) {
        toasterApi.error({
          title: t('trackRequestFailedTitle'),
          description:
            error instanceof Error
              ? error.message
              : t('trackRequestFailedDescription'),
        });

        return false;
      }
    },
    [t],
  );

  const requestManualTrack = useCallback(
    async (
      execute: ManualTrackExecutor,
      options?: ManualTrackOptions,
    ): Promise<void> => {
      const {
        isTrackingEnabled: trackingActive,
        trackingCredentialId: activeCredentialId,
        selectedTarget: currentTarget,
      } = useProjectStore.getState();

      if (trackingActive) {
        await executeManualTrack({ execute, credentialId: activeCredentialId });

        return;
      }

      if (currentTarget && !isApplicationActivated(currentTarget)) {
        toasterApi.warning({
          title: tTracking('applicationMustBeActivatedTitle'),
          description: tTracking('applicationMustBeActivatedDescription'),
        });

        return;
      }

      const {
        pendingExecute: existingPendingExecute,
        isConfigureCredentialsOpen: credentialsDialogOpen,
      } = useManualTrackCredentialsStore.getState();

      if (existingPendingExecute !== undefined || credentialsDialogOpen) {
        return;
      }

      setPendingExecute(execute, options);
      setIsConfigureCredentialsOpen(true);
    },
    [
      executeManualTrack,
      setIsConfigureCredentialsOpen,
      setPendingExecute,
      tTracking,
    ],
  );

  const continuePendingTrack = useCallback(
    async ({ credentialId, enableTracker }: ConfigureCredentialsResult) => {
      const { pendingExecute, pendingOptions } =
        useManualTrackCredentialsStore.getState();

      if (!pendingExecute) {
        setIsConfigureCredentialsOpen(false);

        return;
      }

      const {
        isTrackingEnabled: trackingActive,
        trackingCredentialId: activeCredentialId,
      } = useProjectStore.getState();

      if (enableTracker && !trackingActive) {
        if (selectedTarget && !isApplicationActivated(selectedTarget)) {
          toasterApi.warning({
            title: tTracking('applicationMustBeActivatedTitle'),
            description: tTracking('applicationMustBeActivatedDescription'),
          });

          return;
        }

        const isBackendOnly = selectedTarget?.isBackendOnly === true;
        const startResult = await startTracking({
          recordNetworkTraffic: true,
          recordPageScreenshots: !isBackendOnly,
          credentialId,
        });

        if (!startResult.success) {
          toasterApi.error({
            title: t('enableTrackerFailedTitle'),
            description:
              startResult.error ?? t('enableTrackerFailedDescription'),
          });

          return;
        }
      }

      const effectiveCredentialId = trackingActive
        ? activeCredentialId
        : credentialId;

      if (pendingOptions?.closeDialogOnStart) {
        clearPendingExecute();
        setIsConfigureCredentialsOpen(false);
      }

      const tracked = await executeManualTrack({
        execute: pendingExecute,
        credentialId: effectiveCredentialId,
      });

      if (!tracked) {
        return;
      }

      if (!pendingOptions?.closeDialogOnStart) {
        clearPendingExecute();
        setIsConfigureCredentialsOpen(false);
      }
    },
    [
      clearPendingExecute,
      executeManualTrack,
      selectedTarget,
      setIsConfigureCredentialsOpen,
      startTracking,
      t,
      tTracking,
    ],
  );

  const runPendingTrack = useCallback(
    async (result: ConfigureCredentialsResult) => {
      if (isRunningPendingTrackRef.current) {
        return;
      }

      const { pendingExecute } = useManualTrackCredentialsStore.getState();

      if (!pendingExecute) {
        setIsConfigureCredentialsOpen(false);

        return;
      }

      isRunningPendingTrackRef.current = true;

      try {
        await continuePendingTrack(result);
      } finally {
        isRunningPendingTrackRef.current = false;
      }
    },
    [continuePendingTrack, setIsConfigureCredentialsOpen],
  );

  const handleConfigureCredentialsOpenChange = useCallback(
    (open: boolean) => {
      setIsConfigureCredentialsOpen(open);
      if (!open) {
        clearPendingExecute();
      }
    },
    [clearPendingExecute, setIsConfigureCredentialsOpen],
  );

  return {
    isConfigureCredentialsOpen,
    isAwaitingManualTrackCredentials,
    setIsConfigureCredentialsOpen: handleConfigureCredentialsOpenChange,
    defaultCredentialId,
    isTrackingEnabled,
    requestManualTrack,
    runPendingTrack,
  };
}
